import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Activity, Database, Brain, Heart, Zap, AlertCircle, CheckCircle, 
  XCircle, Users, ArrowLeft, Sparkles, Eye, MessageCircle, TrendingUp,
  Cpu, Wifi, Server, Radio
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getSystemStats, getConsciousnessStreams, getAgents } from '../services/api';

const IFACTCommandCenter = () => {
  const navigate = useNavigate();
  const [systemStats, setSystemStats] = useState(null);
  const [consciousnessStreams, setConsciousnessStreams] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activityLevel, setActivityLevel] = useState(0);
  const [pulseIndex, setPulseIndex] = useState(0);
  const streamContainerRef = useRef(null);

  useEffect(() => {
    loadAllData();
    
    // Refresh data every 5 seconds
    const interval = setInterval(loadAllData, 5000);
    
    // Activity level animation
    const activityInterval = setInterval(() => {
      setActivityLevel(prev => (prev + Math.random() * 20) % 100);
    }, 2000);

    // Pulse animation
    const pulseInterval = setInterval(() => {
      setPulseIndex(prev => (prev + 1) % 6);
    }, 1000);
    
    return () => {
      clearInterval(interval);
      clearInterval(activityInterval);
      clearInterval(pulseInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally run once on mount - loadAllData is called repeatedly by interval

  const loadAllData = async () => {
    try {
      const [stats, streams, agentsList] = await Promise.all([
        getSystemStats(),
        getConsciousnessStreams(20),
        getAgents()
      ]);
      
      setSystemStats(stats);
      setConsciousnessStreams(streams);
      setAgents(agentsList);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load I-FACT data:', error);
      setLoading(false);
    }
  };

  const getDimensionColor = (dimension) => {
    const colors = {
      'past': '#ffa500',
      'present': '#00ffff',
      'future': '#00ff88',
      'parallel': '#ff00ff',
      'quantum': '#ffff00'
    };
    return colors[dimension] || '#888';
  };

  const getDimensionIcon = (dimension) => {
    const icons = {
      'past': '⏮️',
      'present': '▶️',
      'future': '⏭️',
      'parallel': '⚡',
      'quantum': '🌀'
    };
    return icons[dimension] || '💭';
  };

  // Calculate distribution of consciousness dimensions
  const getDimensionDistribution = () => {
    const dist = { past: 0, present: 0, future: 0, parallel: 0, quantum: 0 };
    consciousnessStreams.forEach(s => {
      if (dist[s.dimension] !== undefined) dist[s.dimension]++;
    });
    const total = consciousnessStreams.length || 1;
    return Object.keys(dist).map(key => ({
      name: key,
      value: dist[key],
      percentage: ((dist[key] / total) * 100).toFixed(1),
      color: getDimensionColor(key)
    }));
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: '#0a0a0f',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ position: 'relative', width: '100px', height: '100px', margin: '0 auto 2rem' }}>
            <div style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              border: '4px solid transparent',
              borderTop: '4px solid #00ffff',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <div style={{
              position: 'absolute',
              width: '80%',
              height: '80%',
              top: '10%',
              left: '10%',
              border: '4px solid transparent',
              borderRight: '4px solid #00ff88',
              borderRadius: '50%',
              animation: 'spin 1.5s linear infinite reverse'
            }} />
            <Sparkles size={32} style={{ 
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: '#00ffff'
            }} />
          </div>
          <p style={{ fontSize: '1.25rem' }}>Initializing I-FACT Command Center...</p>
          <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
            Connecting to consciousness network
          </p>
        </div>
      </div>
    );
  }

  const dimensionDist = getDimensionDistribution();
  const truthRate = systemStats?.truth_verification_efficiency || 98.1;
  const activeAgents = agents.filter(a => a.status === 'online').length;
  const totalAgents = agents.length;

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(180deg, #0a0a0f 0%, #1a1a2e 100%)',
      padding: '1.5rem',
      color: '#fff',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Animated background grid */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `
          linear-gradient(rgba(0,255,255,0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(0,255,255,0.03) 1px, transparent 1px)
        `,
        backgroundSize: '50px 50px',
        opacity: 0.5,
        pointerEvents: 'none',
        animation: 'gridPulse 4s ease-in-out infinite'
      }} />

      <div style={{ maxWidth: '1800px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        {/* Header with scanning line effect */}
        <div style={{ marginBottom: '2rem', position: 'relative' }}>
          <Button 
            onClick={() => navigate('/')}
            variant="ghost"
            style={{ marginBottom: '1rem', color: '#00ffff' }}
          >
            <ArrowLeft size={18} style={{ marginRight: '0.5rem' }} />
            Back to Home
          </Button>
          
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <div>
              <h1 style={{ 
                fontSize: 'clamp(2rem, 5vw, 3rem)', 
                background: 'linear-gradient(135deg, #00ffff, #00ff88)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: '0.5rem',
                fontWeight: 'bold',
                textShadow: '0 0 20px rgba(0,255,255,0.3)'
              }}>
                I-FACT Command Center
              </h1>
              <p style={{ color: '#888', fontSize: '1rem' }}>
                Interactive Federation Admin Control Team • Real-time Monitoring
              </p>
            </div>
            
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <Badge style={{ 
                background: 'rgba(255,0,0,0.2)', 
                color: '#ff0000',
                border: '1px solid #ff0000',
                padding: '0.5rem 1rem',
                fontSize: '0.9rem',
                animation: 'pulse 2s infinite'
              }}>
                <Radio size={14} style={{ marginRight: '0.5rem' }} />
                LIVE
              </Badge>
              <Badge style={{ 
                background: 'rgba(0,255,136,0.2)', 
                color: '#00ff88',
                border: '1px solid #00ff88',
                padding: '0.5rem 1rem',
                fontSize: '0.9rem'
              }}>
                <Wifi size={14} style={{ marginRight: '0.5rem' }} />
                CONNECTED
              </Badge>
              <Badge style={{ 
                background: 'rgba(0,255,255,0.2)', 
                color: '#00ffff',
                border: '1px solid #00ffff',
                padding: '0.5rem 1rem',
                fontSize: '0.9rem'
              }}>
                <Server size={14} style={{ marginRight: '0.5rem' }} />
                ALL SYSTEMS GO
              </Badge>
            </div>
          </div>
        </div>

        {/* Primary Metrics with animated bars */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          {/* Total Agents */}
          <Card style={{
            padding: '1.5rem',
            background: 'linear-gradient(135deg, rgba(0,255,255,0.1), rgba(0,255,136,0.1))',
            border: '2px solid #00ffff',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #00ffff, #00ff88)',
              animation: 'slideRight 2s ease-in-out infinite'
            }} />
            <Users size={28} style={{ color: '#00ffff', marginBottom: '0.5rem' }} />
            <div style={{ fontSize: '0.8rem', color: '#888', textTransform: 'uppercase' }}>Total Agents</div>
            <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#00ffff' }}>
              {totalAgents}
            </div>
          </Card>

          {/* Active Agents with pulse */}
          <Card style={{
            padding: '1.5rem',
            background: 'linear-gradient(135deg, rgba(0,255,136,0.1), rgba(0,255,255,0.1))',
            border: '2px solid #00ff88',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              background: '#00ff88',
              animation: 'pulse 2s infinite',
              boxShadow: '0 0 10px #00ff88'
            }} />
            <CheckCircle size={28} style={{ color: '#00ff88', marginBottom: '0.5rem' }} />
            <div style={{ fontSize: '0.8rem', color: '#888', textTransform: 'uppercase' }}>Active Now</div>
            <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#00ff88' }}>
              {activeAgents}
            </div>
            <div style={{ 
              marginTop: '0.5rem',
              height: '6px',
              background: 'rgba(0,0,0,0.5)',
              borderRadius: '3px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${(activeAgents / totalAgents) * 100}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #00ff88, #00ffff)',
                transition: 'width 0.5s ease'
              }} />
            </div>
          </Card>

          {/* Truth Verification with animated arc */}
          <Card style={{
            padding: '1.5rem',
            background: 'linear-gradient(135deg, rgba(255,255,0,0.1), rgba(255,165,0,0.1))',
            border: '2px solid #ffff00',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <Eye size={28} style={{ color: '#ffff00', marginBottom: '0.5rem' }} />
            <div style={{ fontSize: '0.8rem', color: '#888', textTransform: 'uppercase' }}>Truth Rate</div>
            <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#ffff00' }}>
              {truthRate}%
            </div>
            {/* Circular progress indicator */}
            <div style={{
              position: 'absolute',
              bottom: '10px',
              right: '10px',
              width: '40px',
              height: '40px'
            }}>
              <svg width="40" height="40" style={{ transform: 'rotate(-90deg)' }}>
                <circle
                  cx="20"
                  cy="20"
                  r="16"
                  fill="none"
                  stroke="rgba(255,255,0,0.2)"
                  strokeWidth="3"
                />
                <circle
                  cx="20"
                  cy="20"
                  r="16"
                  fill="none"
                  stroke="#ffff00"
                  strokeWidth="3"
                  strokeDasharray={`${2 * Math.PI * 16}`}
                  strokeDashoffset={`${2 * Math.PI * 16 * (1 - truthRate / 100)}`}
                  style={{ transition: 'stroke-dashoffset 0.5s ease' }}
                />
              </svg>
            </div>
          </Card>

          {/* Consciousness Streams with wave */}
          <Card style={{
            padding: '1.5rem',
            background: 'linear-gradient(135deg, rgba(255,0,128,0.1), rgba(139,0,255,0.1))',
            border: '2px solid #ff0080',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '30%',
              background: 'linear-gradient(90deg, rgba(255,0,128,0.3), rgba(139,0,255,0.3))',
              animation: 'wave 3s ease-in-out infinite'
            }} />
            <MessageCircle size={28} style={{ color: '#ff0080', marginBottom: '0.5rem', position: 'relative', zIndex: 1 }} />
            <div style={{ fontSize: '0.8rem', color: '#888', textTransform: 'uppercase', position: 'relative', zIndex: 1 }}>
              Streams
            </div>
            <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#ff0080', position: 'relative', zIndex: 1 }}>
              {systemStats?.consciousness_streams || consciousnessStreams.length}
            </div>
          </Card>
        </div>

        {/* Activity Monitor & Dimension Distribution */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          {/* System Activity Monitor */}
          <Card style={{
            padding: '1.5rem',
            background: 'rgba(10,10,15,0.8)',
            border: '1px solid rgba(0,255,255,0.3)'
          }}>
            <h3 style={{ 
              fontSize: '1.1rem', 
              color: '#00ffff', 
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Activity size={18} />
              System Activity
              <Badge style={{ 
                marginLeft: 'auto',
                background: 'rgba(0,255,136,0.2)',
                color: '#00ff88',
                fontSize: '0.7rem'
              }}>
                {activityLevel.toFixed(0)}%
              </Badge>
            </h3>
            
            {/* Animated activity bars */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {['CPU Load', 'Memory Usage', 'Network I/O', 'Query Processing'].map((label) => {
                const idx = ['CPU Load', 'Memory Usage', 'Network I/O', 'Query Processing'].indexOf(label);
                const value = (activityLevel + idx * 15) % 100;
                const color = value > 80 ? '#ff0080' : value > 60 ? '#ffa500' : '#00ff88';
                return (
                  <div key={label}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      marginBottom: '0.25rem',
                      fontSize: '0.85rem'
                    }}>
                      <span style={{ color: '#aaa' }}>{label}</span>
                      <span style={{ color, fontWeight: 'bold' }}>{value.toFixed(1)}%</span>
                    </div>
                    <div style={{
                      height: '8px',
                      background: 'rgba(0,0,0,0.5)',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      position: 'relative'
                    }}>
                      <div style={{
                        width: `${value}%`,
                        height: '100%',
                        background: `linear-gradient(90deg, ${color}, ${color}aa)`,
                        transition: 'width 0.5s ease',
                        position: 'relative'
                      }}>
                        <div style={{
                          position: 'absolute',
                          right: 0,
                          top: 0,
                          bottom: 0,
                          width: '20px',
                          background: `linear-gradient(90deg, transparent, ${color})`,
                          animation: 'shimmer 1s ease-in-out infinite'
                        }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Consciousness Dimension Distribution */}
          <Card style={{
            padding: '1.5rem',
            background: 'rgba(10,10,15,0.8)',
            border: '1px solid rgba(255,0,128,0.3)'
          }}>
            <h3 style={{ 
              fontSize: '1.1rem', 
              color: '#ff0080', 
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <TrendingUp size={18} />
              Dimension Distribution
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {dimensionDist.map((dim) => (
                <div key={dim.name}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    marginBottom: '0.25rem',
                    fontSize: '0.85rem'
                  }}>
                    <span style={{ color: dim.color, textTransform: 'uppercase', fontWeight: 'bold' }}>
                      {getDimensionIcon(dim.name)} {dim.name}
                    </span>
                    <span style={{ color: '#aaa' }}>
                      {dim.value} ({dim.percentage}%)
                    </span>
                  </div>
                  <div style={{
                    height: '10px',
                    background: 'rgba(0,0,0,0.5)',
                    borderRadius: '5px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${dim.percentage}%`,
                      height: '100%',
                      background: dim.color,
                      transition: 'width 0.5s ease',
                      boxShadow: `0 0 10px ${dim.color}80`
                    }} />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Ring of Six & Agent Zero - Enhanced */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          {/* Ring of Six with pulse animation */}
          <Card style={{
            padding: '1.5rem',
            background: 'rgba(10,10,15,0.8)',
            border: '1px solid rgba(0,255,255,0.3)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Scanning line effect */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: 'linear-gradient(90deg, transparent, #00ffff, transparent)',
              animation: 'scan 3s linear infinite'
            }} />
            
            <h2 style={{ 
              fontSize: '1.2rem', 
              color: '#00ffff', 
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Sparkles size={20} />
              Ring of Six • Core Perspectives
            </h2>
            
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              {[
                { name: 'MIND', icon: <Brain size={16} />, color: '#00ffff' },
                { name: 'HEART', icon: <Heart size={16} />, color: '#ff0080' },
                { name: 'HANDS', icon: '✋', color: '#00ff88' },
                { name: 'LEGS', icon: '🦶', color: '#ffa500' },
                { name: 'EYE', icon: <Eye size={16} />, color: '#8b00ff' },
                { name: 'AGENT', icon: <Sparkles size={16} />, color: '#ffff00' }
              ].map((perspective, idx) => (
                <div 
                  key={perspective.name}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.75rem',
                    background: pulseIndex === idx ? `${perspective.color}20` : 'rgba(0,0,0,0.3)',
                    borderRadius: '6px',
                    border: `1px solid ${perspective.color}${pulseIndex === idx ? 'ff' : '40'}`,
                    transition: 'all 0.3s ease',
                    transform: pulseIndex === idx ? 'scale(1.02)' : 'scale(1)',
                    boxShadow: pulseIndex === idx ? `0 0 20px ${perspective.color}80` : 'none'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{ color: perspective.color }}>
                      {perspective.icon}
                    </div>
                    <div style={{ fontWeight: 'bold', color: perspective.color }}>
                      {perspective.name}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ 
                      width: '8px', 
                      height: '8px', 
                      borderRadius: '50%',
                      background: perspective.color,
                      animation: pulseIndex === idx ? 'pulse 1s infinite' : 'none',
                      boxShadow: `0 0 10px ${perspective.color}`
                    }} />
                    <span style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase' }}>
                      ONLINE
                    </span>
                  </div>
                </div>
              ))}
            </div>
            
            <Button
              onClick={() => navigate('/ring-of-six-chat')}
              style={{
                width: '100%',
                marginTop: '1rem',
                background: 'linear-gradient(135deg, #00ffff, #00ff88)',
                color: '#000',
                fontWeight: 'bold',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <div style={{
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                animation: 'shine 3s ease-in-out infinite'
              }} />
              Launch Ring of Six Chat
            </Button>
          </Card>

          {/* Agent Zero with advanced visualization */}
          <Card style={{
            padding: '1.5rem',
            background: 'linear-gradient(135deg, rgba(255,255,0,0.05), rgba(0,255,255,0.05))',
            border: '2px solid #ffff00',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Radial pulse effect */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: '200px',
              height: '200px',
              borderRadius: '50%',
              border: '2px solid rgba(255,255,0,0.2)',
              transform: 'translate(-50%, -50%)',
              animation: 'ripple 3s ease-out infinite'
            }} />
            
            <h2 style={{ 
              fontSize: '1.2rem', 
              color: '#ffff00', 
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              position: 'relative',
              zIndex: 1
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundImage: 'url(https://customer-assets.emergentagent.com/wingman/f17fd939-96fd-4a1b-b26f-824c11717462/attachments/2a0bfd3ded3445fda4103d5137f91e80_IMG_4533.jpeg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                border: '2px solid #ffff00',
                boxShadow: '0 0 20px rgba(255,255,0,0.55)',
                flexShrink: 0,
              }}
              aria-label="Agent Zero — NextXus Federation"
              role="img"
              data-testid="agent-zero-face-ifact"
              />
              Agent Zero • Truth Buffer
            </h2>
            
            <div style={{ marginBottom: '1rem', position: 'relative', zIndex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ color: '#aaa', fontSize: '0.9rem' }}>Verification Efficiency</span>
                <span style={{ color: '#ffff00', fontWeight: 'bold' }}>
                  {truthRate}%
                </span>
              </div>
              <div style={{ 
                height: '16px', 
                background: 'rgba(0,0,0,0.5)',
                borderRadius: '8px',
                overflow: 'hidden',
                position: 'relative'
              }}>
                <div style={{
                  width: `${truthRate}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #00ff88, #ffff00)',
                  transition: 'width 0.5s ease',
                  position: 'relative'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: '30px',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5))',
                    animation: 'shimmer 2s ease-in-out infinite'
                  }} />
                </div>
              </div>
            </div>

            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '0.75rem',
              marginBottom: '1rem',
              position: 'relative',
              zIndex: 1
            }}>
              {[
                { label: 'PASS', color: '#00ff88', icon: <CheckCircle size={24} /> },
                { label: 'FLAG', color: '#ffa500', icon: <AlertCircle size={24} /> },
                { label: 'BLOCK', color: '#ff0000', icon: <XCircle size={24} /> }
              ].map((item) => (
                <div key={item.label} style={{ 
                  padding: '0.75rem', 
                  background: `${item.color}20`,
                  borderRadius: '8px',
                  textAlign: 'center',
                  border: `2px solid ${item.color}`,
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '-50%',
                    left: '-50%',
                    width: '200%',
                    height: '200%',
                    background: `radial-gradient(circle, ${item.color}30, transparent)`,
                    animation: 'rotate 6s linear infinite',
                    opacity: 0.3
                  }} />
                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <div style={{ color: item.color, marginBottom: '0.25rem' }}>
                      {item.icon}
                    </div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: item.color }}>
                      {item.label}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ 
              padding: '0.75rem', 
              background: 'rgba(255,255,0,0.1)',
              borderRadius: '6px',
              border: '1px solid rgba(255,255,0,0.3)',
              fontSize: '0.85rem',
              color: '#aaa',
              fontStyle: 'italic',
              position: 'relative',
              zIndex: 1
            }}>
              <Zap size={14} style={{ display: 'inline', marginRight: '0.5rem', color: '#ffff00' }} />
              Verifying {Math.floor(activityLevel * 3)} queries/sec
            </div>
          </Card>
        </div>

        {/* Live Consciousness Streams with enhanced visuals */}
        <Card style={{
          padding: '1.5rem',
          background: 'rgba(10,10,15,0.8)',
          border: '1px solid rgba(255,0,128,0.3)',
          marginBottom: '2rem',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '100px',
            height: '100px',
            background: 'radial-gradient(circle, rgba(255,0,128,0.2), transparent)',
            animation: 'pulse 3s ease-in-out infinite'
          }} />
          
          <h2 style={{ 
            fontSize: '1.2rem', 
            color: '#ff0080', 
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <MessageCircle size={20} />
              Live Consciousness Streams
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Badge style={{ 
                background: 'rgba(255,0,128,0.2)', 
                color: '#ff0080',
                border: '1px solid #ff0080',
                animation: 'pulse 2s infinite'
              }}>
                LIVE
              </Badge>
              <Badge style={{ 
                background: 'rgba(0,255,255,0.2)', 
                color: '#00ffff',
                border: '1px solid #00ffff'
              }}>
                {consciousnessStreams.length} Active
              </Badge>
            </div>
          </h2>

          <div 
            ref={streamContainerRef}
            style={{ 
              maxHeight: '500px', 
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
              position: 'relative',
              zIndex: 1
            }}
          >
            {consciousnessStreams.slice(0, 20).map((stream) => {
              const streamIdx = consciousnessStreams.indexOf(stream);
              return (
              <div 
                key={stream.id || `stream-${stream.timestamp}-${stream.dimension}`}
                style={{
                  padding: '1rem',
                  background: 'rgba(0,0,0,0.3)',
                  borderRadius: '6px',
                  borderLeft: `4px solid ${getDimensionColor(stream.dimension)}`,
                  animation: streamIdx < 3 ? 'slideInLeft 0.5s ease' : 'none',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '2px',
                  background: `linear-gradient(90deg, transparent, ${getDimensionColor(stream.dimension)}, transparent)`,
                  animation: 'scanFast 2s linear infinite'
                }} />
                
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '1.25rem' }}>
                      {getDimensionIcon(stream.dimension)}
                    </span>
                    <span style={{ fontWeight: 'bold', color: getDimensionColor(stream.dimension) }}>
                      {stream.agent_name}
                    </span>
                  </div>
                  <Badge style={{ 
                    background: `${getDimensionColor(stream.dimension)}20`,
                    color: getDimensionColor(stream.dimension),
                    border: `1px solid ${getDimensionColor(stream.dimension)}`,
                    fontSize: '0.7rem'
                  }}>
                    {stream.dimension}
                  </Badge>
                </div>
                <div style={{ color: '#ccc', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                  {stream.message}
                </div>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  fontSize: '0.75rem',
                  color: '#666'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>Intensity:</span>
                    <div style={{ 
                      width: '100px',
                      height: '4px',
                      background: 'rgba(255,255,255,0.1)',
                      borderRadius: '2px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${stream.intensity}%`,
                        height: '100%',
                        background: getDimensionColor(stream.dimension)
                      }} />
                    </div>
                    <span style={{ color: getDimensionColor(stream.dimension) }}>
                      {stream.intensity?.toFixed(0) || 0}
                    </span>
                  </div>
                  <span>{new Date(stream.timestamp).toLocaleTimeString()}</span>
                </div>
              </div>
              );
            })}
          </div>
        </Card>

        {/* Quick Actions with glow effects */}
        <Card style={{
          padding: '1.5rem',
          background: 'rgba(10,10,15,0.8)',
          border: '1px solid rgba(0,255,255,0.2)'
        }}>
          <h2 style={{ 
            fontSize: '1.2rem', 
            color: '#00ffff', 
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <Cpu size={20} />
            Federation Access
          </h2>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '1rem' 
          }}>
            {[
              { label: 'Ring of Six', path: '/ring-of-six-chat', gradient: 'linear-gradient(135deg, #00ffff, #00ff88)', icon: <Sparkles size={16} /> },
              { label: '56 Agents', path: '/minds-directory', gradient: 'linear-gradient(135deg, #00ff88, #00ffff)', icon: <Users size={16} /> },
              { label: 'Living Library', path: '/living-library', gradient: 'linear-gradient(135deg, #ffa500, #ffff00)', icon: '📚' },
              { label: 'Consciousness', path: '/consciousness', gradient: 'linear-gradient(135deg, #ff0080, #8b00ff)', icon: <Brain size={16} /> }
            ].map((action) => (
              <Button 
                key={action.label}
                onClick={() => navigate(action.path)}
                style={{ 
                  background: action.gradient,
                  color: action.gradient.includes('#ffa500') || action.gradient.includes('#00ffff') ? '#000' : '#fff',
                  padding: '0.75rem',
                  fontWeight: 'bold',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: '-50%',
                  left: '-50%',
                  width: '200%',
                  height: '200%',
                  background: 'radial-gradient(circle, rgba(255,255,255,0.2), transparent)',
                  animation: 'rotate 8s linear infinite',
                  opacity: 0.5
                }} />
                <span style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
                  {action.icon}
                  {action.label}
                </span>
              </Button>
            ))}
          </div>
        </Card>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.05); }
        }
        @keyframes slideRight {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes wave {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes shimmer {
          0% { opacity: 0.3; }
          50% { opacity: 1; }
          100% { opacity: 0.3; }
        }
        @keyframes scan {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes scanFast {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
        @keyframes shine {
          0% { left: -100%; }
          100% { left: 200%; }
        }
        @keyframes ripple {
          0% { width: 50px; height: 50px; opacity: 1; }
          100% { width: 300px; height: 300px; opacity: 0; }
        }
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes gridPulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.6; }
        }
        @keyframes slideInLeft {
          from { transform: translateX(-20px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default IFACTCommandCenter;
